import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import { HiX } from "react-icons/hi";
import fallbackImg from "../../assets/Ashwagandha Botanical Supplement Showcase.png";
import { loadProducts, searchProducts, formatPrice } from "./searchUtils";

const MAX_SUGGESTIONS = 6;

export default function SearchBox({ autoFocus = false, onDone }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  // on the results page, keep the current search text in the box
  const [q, setQ] = useState(location.pathname === "/search" ? params.get("q") || "" : "");
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(null); // null = not loaded yet
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);

  /* load the product list the first time the box is used */
  const ensureProducts = () => {
    if (products) return;
    loadProducts()
      .then((list) => {
        setProducts(list);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  };

  const query = q.trim();
  const matches = useMemo(
    () => (products && query ? searchProducts(products, query) : []),
    [products, query],
  );
  const suggestions = matches.slice(0, MAX_SUGGESTIONS);

  /* close the list when the page changes */
  useEffect(() => {
    setOpen(false);
    setActive(-1);
  }, [location.pathname, location.search]);

  /* close when clicking outside */
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const finish = () => {
    setOpen(false);
    setActive(-1);
    if (onDone) onDone();
  };

  const goToResults = () => {
    if (!query) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
    finish();
  };

  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && suggestions[active]) {
      navigate(`/product/${suggestions[active].id}`);
      finish();
      return;
    }
    goToResults();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showList = open && query.length > 0;

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={submit}
        role="search"
        className="flex items-center gap-3 h-[46px] px-5 rounded-full bg-white border border-gray-200 shadow-[0_1px_6px_rgba(0,0,0,0.06)] focus-within:border-[#2f6b3a] transition-colors"
      >
        <button type="submit" aria-label="Search" className="text-gray-700 shrink-0">
          <CiSearch className="text-[22px]" />
        </button>
        <input
          type="text"
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => {
            ensureProducts();
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search for Ayurveda, wellness, clothing & more..."
          aria-label="Search products"
          aria-expanded={showList}
          aria-controls="search-suggestions"
          autoComplete="off"
          className="flex-1 min-w-0 bg-transparent text-[13px] outline-none placeholder-gray-500"
        />
        {q && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQ("");
              setActive(-1);
            }}
            className="text-gray-400 hover:text-gray-700 shrink-0"
          >
            <HiX className="text-[16px]" />
          </button>
        )}
      </form>

      {showList && (
        <div
          id="search-suggestions"
          role="listbox"
          className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden max-h-[70vh] overflow-y-auto"
        >
          {failed && <p className="px-5 py-4 text-[13px] text-red-600">Could not load products. Try again.</p>}

          {!failed && !products && <p className="px-5 py-4 text-[13px] text-gray-500">Searching...</p>}

          {!failed && products && suggestions.length === 0 && (
            <p className="px-5 py-4 text-[13px] text-gray-500">No products found for “{query}”.</p>
          )}

          {suggestions.map((p, i) => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              role="option"
              aria-selected={i === active}
              onClick={finish}
              onMouseEnter={() => setActive(i)}
              className={`flex items-center gap-3 px-4 py-2.5 ${i === active ? "bg-[#eef5ea]" : "hover:bg-[#f6f8f2]"}`}
            >
              <img
                src={p.img || fallbackImg}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = fallbackImg;
                }}
                className="w-[44px] h-[44px] rounded-md object-cover bg-[#f3f6ee] shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-[#1f2d22] truncate">{p.name}</p>
                {p.category && <p className="text-[12px] text-gray-500 truncate">{p.category}</p>}
              </div>
              <span className="text-[13px] font-semibold text-[#1f2d22] shrink-0">{formatPrice(p.price)}</span>
            </Link>
          ))}

          {!failed && products && matches.length > 0 && (
            <button
              type="button"
              onClick={goToResults}
              className="w-full text-left px-5 py-3 text-[13px] font-semibold text-[#2f6b3a] border-t border-gray-100 hover:bg-[#f6f8f2]"
            >
              See all {matches.length} result{matches.length > 1 ? "s" : ""} for “{query}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
